/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { calculateScaleFactor, Html } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { type FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { type Group, Matrix4, type Mesh, Quaternion, Vector3 } from 'three';

import { context } from 'app/modules/wallforming/components/scenes/tools/pivot-controls/context';
import { EPSILON } from 'shared/constants';

const vec1 = /* @__PURE__ */ new Vector3();
const vec2 = /* @__PURE__ */ new Vector3();

export const calculateOffset = (
    clickPoint: Vector3,
    normal: Vector3,
    rayStart: Vector3,
    rayDir: Vector3
) => {
    const e1 = normal.dot(normal);
    const e2 = normal.dot(clickPoint) - normal.dot(rayStart);
    const e3 = normal.dot(rayDir);

    if (e3 === 0) {
        return -e2 / e1;
    }

    vec1.copy(rayDir)
        .multiplyScalar(e1 / e3)
        .sub(normal);
    vec2.copy(rayDir)
        .multiplyScalar(e2 / e3)
        .add(rayStart)
        .sub(clickPoint);

    const offset = -vec1.dot(vec2) / vec1.dot(vec1);
    return offset;
};

const upV = /* @__PURE__ */ new Vector3(0, 1, 0);
const scaleV = /* @__PURE__ */ new Vector3();
const scaleMatrix = /* @__PURE__ */ new Matrix4();

export const ScalingSphere: FC<{ direction: Vector3; axis: 0 | 1 | 2 }> = ({ direction, axis }) => {
    const {
        scaleLimits,
        annotations,
        annotationsClass,
        depthTest,
        scale,
        lineWidth,
        fixed,
        axisColors,
        hoveredColor,
        selectedColor,
        renderOrder,
        onDragStart,
        onDrag,
        onDragEnd,
        userData,
        selectedControl,
        setSelectedControl
    } = useContext(context);

    const size = useThree((state) => state.size);
    const camControls = useThree((state) => state.controls) as unknown as { enabled: boolean } | undefined;
    const camControlsRef = useRef(camControls);
    const divRef = useRef<HTMLDivElement>(null!);
    const objRef = useRef<Group>(null!);
    const meshRef = useRef<Mesh>(null!);
    const scale0 = useRef<number>(1);
    const scaleCur = useRef<number>(1);
    const clickInfo = useRef<{
        clickPoint: Vector3;
        dir: Vector3;
        mPLG: Matrix4;
        mPLGInv: Matrix4;
        offsetMultiplier: number;
    } | null>(null);
    const gl = useThree((state) => state.gl);
    const [isHovered, setIsHovered] = useState(false);
    const isSelected = selectedControl?.component === 'Sphere' && selectedControl?.axis === axis;
    const color = isHovered
        ? (hoveredColor ?? axisColors[axis])
        : selectedControl
          ? isSelected
              ? (selectedColor ?? axisColors[axis])
              : '#696969'
          : axisColors[axis];

    const opacity = isSelected || isHovered ? 1 : 0.6;

    const position = fixed ? 1.2 : 1.2 * scale;

    const onPointerDown = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            scale0.current = 1;
            scaleCur.current = 1;
            setSelectedControl({ component: 'Sphere', axis });
            if (annotations) {
                divRef.current.innerText = `${scaleCur.current.toFixed(2)}`;
                divRef.current.style.display = 'block';
            }
            e.stopPropagation();
            const rotation = new Matrix4().extractRotation(objRef.current.matrixWorld);
            const clickPoint = e.point.clone();
            const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
            const dir = direction.clone().applyMatrix4(rotation).normalize();
            const mPLG = objRef.current.matrixWorld.clone();
            const mPLGInv = mPLG.clone().invert();
            const offsetMultiplier = fixed
                ? 1 / calculateScaleFactor(objRef.current.getWorldPosition(vec1), scale, e.camera, size)
                : 1;
            clickInfo.current = { clickPoint, dir, mPLG, mPLGInv, offsetMultiplier };
            onDragStart({ component: 'Sphere', axis, origin, directions: [dir] });
            camControls && camControlsRef.current && (camControlsRef.current.enabled = false);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.setPointerCapture(e.pointerId);
        },
        [setSelectedControl, axis, annotations, direction, fixed, scale, size, onDragStart, camControls]
    );

    const onPointerMove = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            gl.domElement.style.setProperty('cursor', 'pointer');
            if (!isHovered) {
                setIsHovered(true);
            }

            if (clickInfo.current) {
                const { clickPoint, dir, mPLG, mPLGInv, offsetMultiplier } = clickInfo.current;
                const [min, max] = scaleLimits?.[axis] || [EPSILON, undefined];

                const offsetW = calculateOffset(clickPoint, dir, e.ray.origin, e.ray.direction);
                const offsetL = offsetW * offsetMultiplier;
                const offsetH = fixed ? offsetL : offsetL / scale;
                let upscale = Math.pow(2, offsetH * 0.5);

                if (e.shiftKey) {
                    upscale = Math.round(upscale * 10) / 10;
                }

                upscale = Math.max(upscale, min / scale0.current);
                if (max !== undefined) {
                    upscale = Math.min(upscale, max / scale0.current);
                }
                scaleCur.current = scale0.current * upscale;
                meshRef.current.position.set(0, position + offsetL, 0);
                if (annotations) {
                    divRef.current.innerText = `${scaleCur.current.toFixed(2)}`;
                }
                scaleV.set(1, 1, 1);
                scaleV.setComponent(axis, upscale);
                scaleMatrix.makeScale(scaleV.x, scaleV.y, scaleV.z).premultiply(mPLG).multiply(mPLGInv);
                onDrag(scaleMatrix);
            }
        },
        [gl.domElement.style, isHovered, scaleLimits, axis, fixed, scale, position, annotations, onDrag]
    );

    const onPointerUp = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (annotations) {
                divRef.current.style.display = 'none';
            }
            e.stopPropagation();
            scale0.current = scaleCur.current;
            clickInfo.current = null;
            meshRef.current.position.set(0, position, 0);
            onDragEnd();
            camControls && camControlsRef.current && (camControlsRef.current.enabled = true);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.releasePointerCapture(e.pointerId);
        },
        [annotations, camControls, onDragEnd, position]
    );

    const onPointerOut = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            setIsHovered(false);
            gl.domElement.style.setProperty('cursor', 'auto');
        },
        [gl.domElement.style]
    );

    const { radius, matrixL } = useMemo(() => {
        const radius = fixed ? (lineWidth / scale) * 1.8 : scale / 22.5;
        const quaternion = new Quaternion().setFromUnitVectors(upV, direction.clone().normalize());
        const matrixL = new Matrix4().makeRotationFromQuaternion(quaternion);
        return { radius, matrixL };
    }, [direction, scale, lineWidth, fixed]);

    return (
        <group ref={objRef} userData={{ ...userData, isPivotControl: true }}>
            <group
                matrix={matrixL}
                matrixAutoUpdate={false}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerOut={onPointerOut}
                onPointerUp={onPointerUp}
            >
                {annotations && (
                    <Html position={[0, position / 2, 0]}>
                        <div
                            ref={divRef}
                            className={annotationsClass}
                            style={{
                                display: 'none',
                                background: '#151520',
                                color: 'white',
                                padding: '6px 8px',
                                borderRadius: 7,
                                whiteSpace: 'nowrap'
                            }}
                        />
                    </Html>
                )}
                <mesh ref={meshRef} position={[0, position, 0]} renderOrder={renderOrder} userData={userData}>
                    <sphereGeometry args={[radius, 12, 12]} />
                    <meshBasicMaterial
                        color={color}
                        depthTest={depthTest}
                        opacity={opacity}
                        polygonOffsetFactor={-10}
                        polygonOffset
                        transparent
                    />
                </mesh>
            </group>
        </group>
    );
};
